import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Converter } from 'opencc-js';

export type Locale = 'zh_TW' | 'zh_CN';

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const STORAGE_KEY = 'edye-locale';
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT']);

const LocaleContext = createContext<LocaleContextType>({
  locale: 'zh_TW',
  setLocale: () => {},
});

export const useLocale = () => useContext(LocaleContext);

/** Read persisted locale (usable outside React) */
export function getStoredLocale(): Locale {
  return (localStorage.getItem(STORAGE_KEY) as Locale) || 'zh_TW';
}

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale] = useState<Locale>(getStoredLocale);

  const setLocale = useCallback((next: Locale) => {
    if (next !== locale) {
      localStorage.setItem(STORAGE_KEY, next);
      window.location.reload();
    }
  }, [locale]);

  // DOM-level tw→cn conversion via MutationObserver
  useEffect(() => {
    if (locale !== 'zh_CN') return;

    const convert = Converter({ from: 'tw', to: 'cn' });

    function convertText(node: Text) {
      const t = node.textContent;
      if (!t) return;
      const c = convert(t);
      if (t !== c) node.textContent = c;
    }

    function convertAttrs(el: Element) {
      for (const attr of ['placeholder', 'title', 'aria-label']) {
        const v = el.getAttribute(attr);
        if (v) {
          const c = convert(v);
          if (v !== c) el.setAttribute(attr, c);
        }
      }
    }

    function walk(root: Node) {
      const tw = document.createTreeWalker(
        root,
        NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
        {
          acceptNode(n) {
            if (n.nodeType === Node.ELEMENT_NODE && SKIP_TAGS.has((n as Element).tagName))
              return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
          },
        },
      );
      while (tw.nextNode()) {
        const n = tw.currentNode;
        if (n.nodeType === Node.TEXT_NODE) convertText(n as Text);
        else if (n.nodeType === Node.ELEMENT_NODE) convertAttrs(n as Element);
      }
    }

    // Convert existing DOM
    walk(document.body);

    // Watch for React re-renders / dynamic content
    const obs = new MutationObserver((muts) => {
      obs.disconnect();
      for (const m of muts) {
        if (m.type === 'childList') {
          m.addedNodes.forEach((n) => {
            if (n.nodeType === Node.TEXT_NODE) convertText(n as Text);
            else if (n.nodeType === Node.ELEMENT_NODE) walk(n);
          });
        } else if (m.type === 'characterData' && m.target.nodeType === Node.TEXT_NODE) {
          convertText(m.target as Text);
        }
      }
      obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    });

    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
};
